import type {
    LexicalCommand, LexicalEditor, LexicalNode, RangeSelection,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $filter,
    $getNearestBlockElementAncestorOrThrow,
    mergeRegister,
} from '@lexical/utils';
import {
    $createRangeSelection,
    $getSelection,
    $isBlockElementNode,
    $isRangeSelection,
    $normalizeSelection__EXPERIMENTAL,
    COMMAND_PRIORITY_CRITICAL,
    COMMAND_PRIORITY_EDITOR,
    INDENT_CONTENT_COMMAND,
    INSERT_TAB_COMMAND,
    KEY_TAB_COMMAND,
    OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

function $indentOverTab(selection: RangeSelection): boolean {
    const nodes = selection.getNodes();
    const canIndentBlockNodes = $filter(nodes, node => {
        if ($isBlockElementNode(node) && (node).canIndent()) {
            return node;
        }
        return null;
    }) as LexicalNode[];

    if (canIndentBlockNodes.length > 0) {
        return true;
    }

    const { anchor } = selection;
    const { focus } = selection;
    const first = focus.isBefore(anchor) ? focus : anchor;
    const firstNode = first.getNode();
    const firstBlock = $getNearestBlockElementAncestorOrThrow(firstNode);

    if (firstBlock.canIndent()) {
        const firstBlockKey = firstBlock.getKey();
        let selectionAtStart = $createRangeSelection();
        selectionAtStart.anchor.set(firstBlockKey, 0, 'element');
        selectionAtStart.focus.set(firstBlockKey, 0, 'element');
        selectionAtStart = $normalizeSelection__EXPERIMENTAL(selectionAtStart);
        if (selectionAtStart.anchor.is(first)) {
            return true;
        }
    }

    return false;
}

const registerTabIndentation = (
    editor: LexicalEditor,
    maxIndent?: number,
) => mergeRegister(
    editor.registerCommand<KeyboardEvent>(
        KEY_TAB_COMMAND,
        event => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
                return false;
            }
            event.preventDefault();
            let command: LexicalCommand<void>;
            if ($indentOverTab(selection)) {
                command = event.shiftKey ? OUTDENT_CONTENT_COMMAND : INDENT_CONTENT_COMMAND;
            } else {
                command = INSERT_TAB_COMMAND;
            }
            return editor.dispatchCommand(command, undefined);
        },
        COMMAND_PRIORITY_EDITOR,
    ),

    editor.registerCommand(
        INDENT_CONTENT_COMMAND,
        () => {
            if (maxIndent == null) {
                return false;
            }

            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
                return false;
            }

            const indents = selection
                .getNodes()
                .map(node => $getNearestBlockElementAncestorOrThrow(node).getIndent());

            // Check if any node would exceed maxIndent
            const wouldExceedMaxIndent = indents.some(indent => indent + 1 >= maxIndent);
            return wouldExceedMaxIndent;
        },
        COMMAND_PRIORITY_CRITICAL,
    ),
);

/**
 * This plugin adds the ability to indent content using the tab key. The library doesn't
 * recommend using this plugin as it could negatively affect accessibility for keyboard
 * users, causing focus to become trapped within the editor.
 */
const TabIndentationPlugin = ({ maxIndent }: { maxIndent?: number }): null => {
    const [editor] = useLexicalComposerContext();
    useEffect(() => registerTabIndentation(editor, maxIndent), [editor, maxIndent]);

    return null;
};

export default TabIndentationPlugin;
